import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight,
  ArrowRight,
  Zap,
  Percent,
  Headphones,
  TrendingUp,
  ShieldCheck,
  FileCheck2,
  CheckCircle2,
  Menu,
  X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERKS = [
  {
    Icon: Zap,
    title: "Close in 7–10 days",
    desc: "Your borrowers get to the table fast — and you get paid faster. No 60-day underwriting queues.",
  },
  {
    Icon: Percent,
    title: "Competitive comp",
    desc: "Origination, YSP, and processing fees structured to reward you for bringing strong, qualified deals.",
  },
  {
    Icon: Headphones,
    title: "One point of contact",
    desc: "A dedicated underwriter and processor on every file. No call centers, no transfers, no waiting on hold.",
  },
  {
    Icon: TrendingUp,
    title: "Full product suite",
    desc: "Fix & Flip, DSCR, New Construction, Bridge, Transactional, EMD, and Commercial — all in-house.",
  },
  {
    Icon: ShieldCheck,
    title: "Direct lender",
    desc: "We use our own capital and underwrite in-house. No table-funding games, no surprise pricing changes at close.",
  },
  {
    Icon: FileCheck2,
    title: "Clean process",
    desc: "Term sheet in 24 hours. Doc requests sent once. Closings that don't fall apart in the last 48 hours.",
  },
];

const STEPS = [
  { n: "01", title: "Apply", desc: "Submit the partner form below. Quick review, no commitment." },
  { n: "02", title: "Get Approved", desc: "We review your NMLS, licensing, and recent deals. Approval in 1–2 business days." },
  { n: "03", title: "Submit Deals", desc: "Use your dedicated underwriter contact and the standard intake form to send us scenarios." },
  { n: "04", title: "Get Paid", desc: "Closings funded fast, comp wired at close. Repeat." },
];

const Nav = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed top-0 left-0 right-0 z-50 nav-blur" data-testid="brokers-nav">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-[72px]">
        <Link to="/" className="flex items-center gap-2" data-testid="brokers-nav-logo">
          <img src="/brand/groundup-logo.png" alt="GroundUp Lending" className="h-9 md:h-10 w-auto" />
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          <Link to="/" className="text-sm font-medium text-neutral-700 hover:text-[#22C55E] transition-colors">Home</Link>
          <Link to="/#products" className="text-sm font-medium text-neutral-700 hover:text-[#22C55E] transition-colors">Loan Products</Link>
          <Link to="/#process" className="text-sm font-medium text-neutral-700 hover:text-[#22C55E] transition-colors">Process</Link>
          <span className="text-sm font-medium text-[#22C55E]">Brokers</span>
        </nav>
        <div className="hidden md:block">
          <a href="#broker-apply" className="btn-primary" data-testid="brokers-nav-cta">
            Apply to Partner <ArrowRight size={16} />
          </a>
        </div>
        <button className="md:hidden p-2" onClick={() => setOpen(!open)} aria-label="Toggle menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-neutral-200 bg-white">
          <div className="px-6 py-4 flex flex-col gap-4">
            <Link to="/" className="text-base font-medium" onClick={() => setOpen(false)}>Home</Link>
            <Link to="/#products" className="text-base font-medium" onClick={() => setOpen(false)}>Loan Products</Link>
            <Link to="/#process" className="text-base font-medium" onClick={() => setOpen(false)}>Process</Link>
            <a href="#broker-apply" onClick={() => setOpen(false)} className="btn-primary w-full justify-center">
              Apply to Partner <ArrowRight size={16} />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

const Hero = () => (
  <section className="relative pt-[72px] bg-[#0A0A0A] text-white overflow-hidden">
    <div
      className="absolute inset-0 opacity-50"
      style={{
        backgroundImage:
          "url('https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/95 via-[#0A0A0A]/75 to-[#0A0A0A]/45" />
    <div className="absolute inset-0 bg-grid opacity-30" />

    <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
      <div className="max-w-3xl">
        <div className="label-overline text-[#22C55E] mb-6 flex items-center gap-3">
          <span className="inline-block w-8 h-px bg-[#22C55E]" />
          Broker Partner Program
        </div>
        <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-[80px] font-bold leading-[0.95] tracking-tight">
          Send the deal.
          <br />
          <span className="text-[#22C55E]">We'll close it.</span>
          <br />
          You get paid.
        </h1>
        <p className="mt-8 max-w-2xl text-lg md:text-xl text-neutral-300 leading-relaxed">
          GroundUp Lending partners with mortgage brokers, loan officers, and capital advisors nationwide.
          Competitive comp, 7–10 day closings, and the same dedicated underwriter on every file. Your borrowers get serviced. You get paid faster.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <a href="#broker-apply" className="btn-primary" data-testid="brokers-hero-cta">
            Apply to Partner <ArrowUpRight size={18} />
          </a>
          <Link to="/#products" className="btn-ghost">
            <span>View Loan Products</span>
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const Perks = () => (
  <section className="bg-[#F4F4F0] py-24 md:py-32" data-testid="brokers-perks">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
        <div className="md:col-span-7">
          <div className="label-overline mb-5 flex items-center gap-3 text-neutral-500">
            <span className="inline-block w-8 h-px bg-neutral-400" />
            01 — Why Partner With Us
          </div>
          <h2 className="font-heading text-4xl md:text-6xl font-bold leading-[1.02] tracking-tight">
            We pay our brokers
            <br />
            <span className="text-[#22C55E]">like real partners.</span>
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="text-lg text-neutral-600 leading-relaxed">
            We were built by investors and underwriters who came up through the broker channel. So we know what brokers actually need:
            speed, real comp, and a lender that doesn't ghost on the back-end.
          </p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 border-t border-l border-neutral-300">
        {PERKS.map((p, i) => (
          <div
            key={p.title}
            className="p-8 md:p-10 border-r border-b border-neutral-300 bg-white"
            data-testid={`broker-perk-${i}`}
          >
            <div className="w-12 h-12 bg-[#22C55E] flex items-center justify-center mb-6">
              <p.Icon size={22} className="text-white" />
            </div>
            <h3 className="font-heading text-2xl font-bold mb-3">{p.title}</h3>
            <p className="text-[15px] leading-relaxed text-neutral-600">{p.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Process = () => (
  <section className="bg-[#0A0A0A] text-white py-24 md:py-32" data-testid="brokers-process">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
        <div className="md:col-span-7">
          <div className="label-overline text-neutral-400 mb-5 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-neutral-500" />
            02 — How It Works
          </div>
          <h2 className="font-heading text-4xl md:text-6xl font-bold leading-[1.02] tracking-tight">
            Four steps from
            <br />
            application to <span className="text-[#22C55E]">first commission.</span>
          </h2>
        </div>
      </div>
      <div className="grid md:grid-cols-4 border-t border-neutral-800">
        {STEPS.map((s) => (
          <div
            key={s.n}
            className="border-b md:border-b-0 md:border-r border-neutral-800 last:border-r-0 p-8 md:p-10 bg-[#0A0A0A]"
          >
            <div className="font-heading text-6xl font-bold text-[#22C55E] mb-6">{s.n}</div>
            <h3 className="font-heading text-xl md:text-2xl font-bold mb-3">{s.title}</h3>
            <p className="text-neutral-400 text-[15px] leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ApplyForm = () => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    company: "",
    nmls: "",
    email: "",
    phone: "",
    states_licensed: "",
    years_experience: "1-3 years",
    monthly_volume: "Under $500K",
    loan_types: "",
    referral_source: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.company || !form.email || !form.phone || !form.states_licensed) {
      toast.error("Please fill in your name, company, email, phone, and states licensed.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/brokers`, form);
      setSubmitted(true);
      toast.success("Broker application received. We'll reach out within 1–2 business days.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="broker-apply" className="bg-[#2C3E6D] text-white py-24 md:py-32" data-testid="broker-apply-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="label-overline text-[#22C55E] mb-5 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-[#22C55E]" />
            03 — Apply
          </div>
          <h2 className="font-heading text-4xl md:text-6xl font-bold leading-[1.02] tracking-tight">
            Become a
            <br />
            <span className="text-[#22C55E]">GroundUp partner.</span>
          </h2>
          <p className="mt-6 text-lg text-white/80 leading-relaxed">
            Submit a quick application. We review NMLS, licensing, and recent deal experience — and respond within 1–2 business days.
            No commitment, no application fee, no soft pull.
          </p>
          <div className="mt-10 space-y-3 text-white/85">
            <div className="flex items-center gap-3 text-sm">
              <CheckCircle2 size={16} className="text-[#22C55E]" /> Open to NMLS-licensed brokers & loan officers
            </div>
            <div className="flex items-center gap-3 text-sm">
              <CheckCircle2 size={16} className="text-[#22C55E]" /> Nationwide partnerships — all 50 states
            </div>
            <div className="flex items-center gap-3 text-sm">
              <CheckCircle2 size={16} className="text-[#22C55E]" /> New brokers welcome with strong scenarios
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <div className="border border-white/15 p-10 md:p-14 bg-[#22325A]" data-testid="broker-success">
              <CheckCircle2 size={40} className="text-[#22C55E] mb-6" />
              <h3 className="font-heading text-3xl md:text-4xl font-bold mb-4">Application received.</h3>
              <p className="text-white/85 text-lg mb-8 leading-relaxed">
                Thanks for applying to the GroundUp Lending broker program. A partner manager will reach out within 1–2 business days
                to verify your licensing and set up your portal access.
              </p>
              <button onClick={() => setSubmitted(false)} className="btn-ghost" data-testid="broker-reset">
                Submit Another <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="border border-white/15 p-8 md:p-12 bg-[#22325A] space-y-6" data-testid="broker-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="label-overline text-white/60 mb-2 block">First Name *</label>
                  <input
                    name="first_name"
                    value={form.first_name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Jane"
                    data-testid="broker-first-name"
                  />
                </div>
                <div>
                  <label className="label-overline text-white/60 mb-2 block">Last Name *</label>
                  <input
                    name="last_name"
                    value={form.last_name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Smith"
                    data-testid="broker-last-name"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="label-overline text-white/60 mb-2 block">Company *</label>
                  <input
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Smith Capital Group"
                    data-testid="broker-company"
                  />
                </div>
                <div>
                  <label className="label-overline text-white/60 mb-2 block">NMLS #</label>
                  <input
                    name="nmls"
                    value={form.nmls}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="1234567"
                    data-testid="broker-nmls"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="label-overline text-white/60 mb-2 block">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="jane@smithcapital.com"
                    data-testid="broker-email"
                  />
                </div>
                <div>
                  <label className="label-overline text-white/60 mb-2 block">Phone *</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="(555) 555-1234"
                    data-testid="broker-phone"
                  />
                </div>
              </div>
              <div>
                <label className="label-overline text-white/60 mb-2 block">States Licensed *</label>
                <input
                  name="states_licensed"
                  value={form.states_licensed}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="NC, SC, GA, FL"
                  data-testid="broker-states"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="label-overline text-white/60 mb-2 block">Years in Business</label>
                  <select
                    name="years_experience"
                    value={form.years_experience}
                    onChange={handleChange}
                    className="form-input"
                    data-testid="broker-years"
                  >
                    {["< 1 year", "1-3 years", "3-5 years", "5-10 years", "10+ years"].map((o) => (
                      <option key={o} value={o} className="bg-[#22325A]">{o}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label-overline text-white/60 mb-2 block">Monthly Volume</label>
                  <select
                    name="monthly_volume"
                    value={form.monthly_volume}
                    onChange={handleChange}
                    className="form-input"
                    data-testid="broker-volume"
                  >
                    {["Under $500K", "$500K – $2M", "$2M – $5M", "$5M – $10M", "$10M+"].map((o) => (
                      <option key={o} value={o} className="bg-[#22325A]">{o}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="label-overline text-white/60 mb-2 block">Loan Types of Interest</label>
                <input
                  name="loan_types"
                  value={form.loan_types}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Fix & Flip, DSCR, New Construction…"
                  data-testid="broker-loan-types"
                />
              </div>
              <div>
                <label className="label-overline text-white/60 mb-2 block">How did you hear about us?</label>
                <input
                  name="referral_source"
                  value={form.referral_source}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Referral / Google / Trade show / etc."
                  data-testid="broker-referral"
                />
              </div>
              <div>
                <label className="label-overline text-white/60 mb-2 block">Notes</label>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  rows={3}
                  className="form-input resize-none"
                  placeholder="Tell us about your typical borrower or any deals you'd like to discuss."
                  data-testid="broker-notes"
                />
              </div>
              <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="broker-submit">
                {submitting ? "Submitting…" : "Submit Application"} <ArrowUpRight size={18} />
              </button>
              <p className="text-xs text-white/50 text-center">
                We'll verify your licensing and respond within 1–2 business days.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="bg-[#0A0A0A] text-white border-t border-neutral-800 py-16">
    <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-3">
        <img src="/brand/groundup-logo-light.png" alt="GroundUp Lending" className="h-8 w-auto" />
      </div>
      <div className="flex items-center gap-8 text-sm text-neutral-400">
        <Link to="/" className="hover:text-[#22C55E] transition-colors">Home</Link>
        <Link to="/#products" className="hover:text-[#22C55E] transition-colors">Products</Link>
        <Link to="/#contact" className="hover:text-[#22C55E] transition-colors">Contact</Link>
      </div>
      <div className="text-xs text-neutral-500 font-mono">© {new Date().getFullYear()} GroundUp Lending</div>
    </div>
  </footer>
);

const Brokers = () => {
  return (
    <div className="bg-[#F4F4F0] min-h-screen" data-testid="brokers-page">
      <Nav />
      <Hero />
      <Perks />
      <Process />
      <ApplyForm />
      <Footer />
    </div>
  );
};

export default Brokers;
